import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import { usePagedData } from '../../hooks/usePagedData';
import { invoicesApi } from '../../api/endpoints';
import {
  InvoiceList,
  InvoiceStatusMap,
  InvoiceTypeStringMap,
  InvoiceStatusStringMap,
  BulkOperationResult,
} from '../../types';
import Select from '../../components/common/Select';
import { useOperations } from '../../contexts/OperationsContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  FiSearch,
  FiPlus,
  FiDownload,
  FiUpload,
  FiSend,
  FiX,
  FiFileText,
  FiCheckSquare,
  FiSquare,
  FiTrash2,
  FiAlertCircle,
  FiCheck,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import FormField from '../../components/common/FormField';
import { SkeletonTable } from '../../components/common/Skeleton';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import DateRangePicker from '../../components/common/DateRangePicker';
import ColumnSettings from '../../components/common/ColumnSettings';

const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ops = useOperations();
  const [searchInput, setSearchInput] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkResult, setShowBulkResult] = useState<BulkOperationResult | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{open: boolean; title: string; message: string; onConfirm: () => void}>({
    open: false, title: '', message: '', onConfirm: () => {},
  });

  // Create form state
  const [createData, setCreateData] = useState({
    purchaseOrderId: '',
    invoiceNumber: '',
    invoiceType: 0,
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    paymentTerms: '',
    currency: 'EUR',
    notes: '',
    items: [{ amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
  });
  const [creating, setCreating] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formTouched, setFormTouched] = useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<InvoiceList>(invoicesApi.list, {
    initialSortBy: 'invoiceDate',
    initialSortDesc: true,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleTypeFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        invoiceType: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleRowClick = useCallback(
    (row: InvoiceList) => {
      navigate(`/invoices/${row.id}`);
    },
    [navigate]
  );

  // Selection
  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!data?.items) return;
    setSelectedIds((prev) => {
      if (prev.size === data.items.length) return new Set();
      return new Set(data.items.map((i) => i.id));
    });
  }, [data]);

  // Bulk submit with progress tracking
  const handleBulkSubmit = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDialog({
      open: true,
      title: 'Masowe wysyłanie faktur',
      message: `Czy na pewno wysłać ${count} faktur${count === 1 ? 'ę' : count < 5 ? 'y' : ''} do Amazon? Tylko faktury w statusie "Szkic" zostaną wysłane.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        setBulkSubmitting(true);
        const opId = ops.startOperation({
          type: 'bulk-submit',
          label: `Wysyłanie ${count} faktur do Amazon`,
          progress: { current: 0, total: count },
        });

        try {
          ops.updateProgress(opId, 0, 'Przesyłanie do Amazon API...');
          const response = await invoicesApi.bulkSubmit(Array.from(selectedIds));
          const result: BulkOperationResult = response.data;

          ops.completeOperation(opId, {
            success: result.successCount,
            failed: result.failedCount,
            errors: result.errors.map(e => ({ identifier: e.identifier, error: e.error })),
          });
          setShowBulkResult(result);
          setSelectedIds(new Set());
          refresh();
        } catch (error: any) {
          ops.failOperation(opId, error.response?.data?.message || 'Błąd masowego wysyłania');
          toast.error(error.response?.data?.message || 'Błąd masowego wysyłania');
        } finally {
          setBulkSubmitting(false);
        }
      },
    });
  }, [selectedIds, refresh, ops]);

  // Bulk download with progress
  const handleBulkDownload = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkDownloading(true);
    const count = selectedIds.size;
    const opId = ops.startOperation({
      type: 'bulk-download',
      label: `Pobieranie ${count} faktur PDF`,
      progress: { current: 0, total: count },
    });

    try {
      ops.updateProgress(opId, 0, 'Generowanie archiwum ZIP...');
      const response = await invoicesApi.bulkDownload(Array.from(selectedIds));
      ops.updateProgress(opId, count, 'Pobieranie pliku...');

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktury_${new Date().toISOString().slice(0, 10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      ops.completeOperation(opId, { success: count, failed: 0, errors: [] });
    } catch (error: any) {
      ops.failOperation(opId, error.response?.data?.message || 'Błąd pobierania');
      toast.error(error.response?.data?.message || 'Błąd pobierania');
    } finally {
      setBulkDownloading(false);
    }
  }, [selectedIds, ops]);

  // Export CSV
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response = await invoicesApi.exportCsv({
        ...query,
        ...filters,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktury_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Eksport CSV zakończony');
    } catch (error: any) {
      toast.error('Błąd eksportu CSV');
    } finally {
      setExporting(false);
    }
  }, [query, filters]);

  // Import CSV
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImportFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setImporting(true);
      const opId = ops.startOperation({
        type: 'import',
        label: `Import faktur z ${file.name}`,
        progress: { current: 0, total: 1 },
      });

      try {
        ops.updateProgress(opId, 0, `Przesyłanie pliku ${file.name} (${(file.size / 1024).toFixed(1)} KB)...`);
        const formData = new FormData();
        formData.append('file', file);
        const response = await invoicesApi.importCsv(formData);
        const result: BulkOperationResult = response.data;

        ops.completeOperation(opId, {
          success: result.successCount,
          failed: result.failedCount,
          errors: result.errors.map(e => ({ identifier: e.identifier, error: e.error })),
        });
        setShowBulkResult(result);
        refresh();
      } catch (error: any) {
        ops.failOperation(opId, error.response?.data?.message || 'Błąd importu');
        toast.error(error.response?.data?.message || 'Błąd importu');
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [refresh, ops]
  );

  // Create invoice
  const addItem = useCallback(() => {
    setCreateData((prev) => ({
      ...prev,
      items: [...prev.items, { amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
    }));
  }, []);

  const removeItem = useCallback((index: number) => {
    setCreateData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  }, []);

  const updateItem = useCallback((index: number, field: string, value: any) => {
    setCreateData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  }, []);

  const validateForm = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!createData.purchaseOrderId.trim()) errors.purchaseOrderId = 'ID zamówienia jest wymagane';
    if (!createData.invoiceNumber.trim()) errors.invoiceNumber = 'Numer faktury jest wymagany';
    if (!createData.invoiceDate) errors.invoiceDate = 'Data faktury jest wymagana';
    const validItems = createData.items.filter(i => i.amazonProductId || i.description);
    if (validItems.length === 0) errors.items = 'Dodaj co najmniej jedną pozycję';
    validItems.forEach((item, idx) => {
      if (!item.description.trim()) errors[`item_${idx}_description`] = 'Opis jest wymagany';
      if (item.quantity <= 0) errors[`item_${idx}_quantity`] = 'Ilość musi być > 0';
      if (item.unitPrice <= 0) errors[`item_${idx}_unitPrice`] = 'Cena musi być > 0';
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [createData]);

  const handleCreateSubmit = useCallback(async () => {
    setFormTouched(true);
    if (!validateForm()) {
      toast.warning('Popraw błędy w formularzu');
      return;
    }
    setCreating(true);
    try {
      await invoicesApi.create({
        ...createData,
        items: createData.items.filter((i) => i.amazonProductId || i.description),
      });
      toast.success('Faktura utworzona');
      setShowCreateModal(false);
      setCreateData({
        purchaseOrderId: '',
        invoiceNumber: '',
        invoiceType: 0,
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        paymentTerms: '',
        currency: 'EUR',
        notes: '',
        items: [{ amazonProductId: '', description: '', quantity: 1, unitPrice: 0, taxRate: 23, hsCode: '' }],
      });
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd tworzenia faktury');
    } finally {
      setCreating(false);
    }
  }, [createData, refresh, validateForm]);

  const formatCurrency = (amount: number, currency?: string) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: currency || 'EUR' });

  const formatDate = (dateStr: string | null) =>
    dateStr ? new Date(dateStr).toLocaleDateString('pl-PL') : '-';

  const itemsTotal = createData.items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const itemsTax = createData.items.reduce((sum, i) => sum + i.quantity * i.unitPrice * (i.taxRate / 100), 0);

  const columns = [
    {
      key: '_select',
      header: '',
      render: (row: InvoiceList) => (
        <span
          onClick={(e) => toggleSelect(row.id, e)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          {selectedIds.has(row.id) ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
        </span>
      ),
    },
    {
      key: 'invoiceNumber',
      header: 'Numer faktury',
      sortable: true,
    },
    {
      key: 'purchaseOrderNumber',
      header: 'Numer PO',
    },
    {
      key: 'invoiceType',
      header: 'Typ',
      render: (row: InvoiceList) => {
        const label = InvoiceTypeStringMap[row.invoiceType] || row.invoiceType;
        const cls = row.invoiceType === 'CreditNote' ? 'badge badge-cancelled' :
                    row.invoiceType === 'DebitNote' ? 'badge badge-pending' :
                    'badge badge-active';
        return <span className={cls}>{label}</span>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: InvoiceList) => {
        const label = InvoiceStatusStringMap[row.status] || row.status;
        return <span className={`badge badge-${row.status === 'Draft' ? 'draft' : row.status === 'Submitted' ? 'pending' : row.status === 'Accepted' ? 'confirmed' : row.status === 'Paid' ? 'delivered' : row.status === 'Rejected' ? 'cancelled' : 'draft'}`}>{label}</span>;
      },
    },
    {
      key: 'invoiceDate',
      header: 'Data faktury',
      sortable: true,
      render: (row: InvoiceList) => formatDate(row.invoiceDate),
    },
    {
      key: 'totalAmount',
      header: 'Kwota',
      sortable: true,
      render: (row: InvoiceList) => formatCurrency(row.totalAmount, row.currency),
    },
    {
      key: 'hasPdf',
      header: 'PDF',
      render: (row: InvoiceList) =>
        row.hasPdf ? (
          <span className="text-success" title="PDF dostępny">
            <FiFileText />
          </span>
        ) : (
          <span style={{ opacity: 0.3 }}>-</span>
        ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Faktury</h1>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <FiPlus /> Nowa faktura
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleImportClick}
            disabled={importing}
          >
            <FiUpload /> {importing ? 'Importuję...' : 'Import CSV'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={exporting}
          >
            <FiDownload /> {exporting ? 'Eksportuję...' : 'Eksport CSV'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.invoicesXlsx, `faktury_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleImportFile}
          />
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">
            <FiCheckSquare /> Zaznaczono: {selectedIds.size}
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleBulkSubmit}
            disabled={bulkSubmitting}
          >
            <FiSend /> {bulkSubmitting ? 'Wysyłanie...' : 'Wyślij zaznaczone do Amazon'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleBulkDownload}
            disabled={bulkDownloading}
          >
            <FiDownload /> {bulkDownloading ? 'Pobieranie...' : 'Pobierz PDF zaznaczonych'}
          </button>
          <button
            className="btn btn-link btn-sm"
            onClick={() => setSelectedIds(new Set())}
          >
            <FiX /> Odznacz
          </button>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj faktur..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters" style={{ display: 'flex', gap: '0.5rem' }}>
          <Select
            options={[
              { value: '', label: 'Wszystkie statusy' },
              ...Object.entries(InvoiceStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder="Wszystkie statusy"
          />
          <Select
            options={[
              { value: '', label: 'Wszystkie typy' },
              { value: '0', label: 'Faktura' },
              { value: '1', label: 'Nota kredytowa' },
              { value: '2', label: 'Nota debetowa' },
            ]}
            value={String(filters.invoiceType ?? '')}
            onChange={handleTypeFilter}
            placeholder="Wszystkie typy"
          />
          <button
            className="btn btn-link btn-sm"
            onClick={toggleSelectAll}
            title="Zaznacz/odznacz wszystkie"
          >
            {selectedIds.size === (data?.items?.length || 0) && selectedIds.size > 0
              ? <><FiCheckSquare /> Odznacz</>
              : <><FiSquare /> Zaznacz</>
            }
          </button>
          <ColumnSettings
            columns={columns.filter(c => c.key !== '_select')}
            storageKey="invoices"
            onColumnsChange={setVisibleColumnKeys}
          />
        </div>
      </div>

      {/* Date range filter */}
      <div style={{ marginBottom: '12px' }}>
        <DateRangePicker
          dateFrom={String(filters.dateFrom ?? '')}
          dateTo={String(filters.dateTo ?? '')}
          onDateFromChange={(v) => setFilters((p: any) => ({ ...p, dateFrom: v || undefined }))}
          onDateToChange={(v) => setFilters((p: any) => ({ ...p, dateTo: v || undefined }))}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={10} cols={8} />
      ) : (
        <DataTable
          columns={visibleColumnKeys.length > 0 ? columns.filter(c => visibleColumnKeys.includes(c.key)) : columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
        />
      )}

      {/* Bulk result modal */}
      {showBulkResult && (
        <div className="modal-overlay" onClick={() => setShowBulkResult(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Wynik operacji</h2>
              <button className="btn btn-icon" onClick={() => setShowBulkResult(null)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div className="bulk-result-summary">
                <div className="bulk-stat bulk-stat-success">
                  <FiCheck /> Sukces: {showBulkResult.successCount}
                </div>
                <div className="bulk-stat bulk-stat-error">
                  <FiAlertCircle /> Błędy: {showBulkResult.failedCount}
                </div>
                <div className="bulk-stat">
                  Łącznie: {showBulkResult.totalCount}
                </div>
              </div>
              {showBulkResult.errors.length > 0 && (
                <div className="bulk-errors" style={{ marginTop: '1rem' }}>
                  <h4>Szczegóły błędów:</h4>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Identyfikator</th>
                          <th>Błąd</th>
                        </tr>
                      </thead>
                      <tbody>
                        {showBulkResult.errors.map((err, i) => (
                          <tr key={i}>
                            <td>{err.identifier}</td>
                            <td>{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setShowBulkResult(null)}>
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h2>Nowa faktura</h2>
              <button className="btn btn-icon" onClick={() => setShowCreateModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <FormField label="ID zamówienia zakupu" required error={formTouched ? formErrors.purchaseOrderId : undefined} hint="GUID z systemu zamówień">
                  <input
                    type="text"
                    className="form-control"
                    value={createData.purchaseOrderId}
                    onChange={(e) => setCreateData((p) => ({ ...p, purchaseOrderId: e.target.value }))}
                    placeholder="GUID zamówienia..."
                  />
                </FormField>
                <FormField label="Numer faktury" required error={formTouched ? formErrors.invoiceNumber : undefined} hint="Np. FV/2026/03/0001">
                  <input
                    type="text"
                    className="form-control"
                    value={createData.invoiceNumber}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceNumber: e.target.value }))}
                    placeholder="FV/2026/03/0001"
                  />
                </FormField>
                <FormField label="Typ dokumentu">
                  <select
                    className="form-control"
                    value={createData.invoiceType}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceType: Number(e.target.value) }))}
                  >
                    <option value={0}>Faktura</option>
                    <option value={1}>Nota kredytowa</option>
                    <option value={2}>Nota debetowa</option>
                  </select>
                </FormField>
                <FormField label="Waluta">
                  <select
                    className="form-control"
                    value={createData.currency}
                    onChange={(e) => setCreateData((p) => ({ ...p, currency: e.target.value }))}
                  >
                    <option value="EUR">EUR</option>
                    <option value="PLN">PLN</option>
                    <option value="USD">USD</option>
                    <option value="GBP">GBP</option>
                  </select>
                </FormField>
                <FormField label="Data faktury" required error={formTouched ? formErrors.invoiceDate : undefined}>
                  <input
                    type="date"
                    className="form-control"
                    value={createData.invoiceDate}
                    onChange={(e) => setCreateData((p) => ({ ...p, invoiceDate: e.target.value }))}
                  />
                </FormField>
                <FormField label="Termin płatności" hint="Opcjonalne - termin zapłaty">
                  <input
                    type="date"
                    className="form-control"
                    value={createData.dueDate}
                    onChange={(e) => setCreateData((p) => ({ ...p, dueDate: e.target.value }))}
                  />
                </FormField>
                <FormField label="Warunki płatności" hint="Np. Net 30, Net 60">
                  <input
                    type="text"
                    className="form-control"
                    value={createData.paymentTerms}
                    onChange={(e) => setCreateData((p) => ({ ...p, paymentTerms: e.target.value }))}
                    placeholder="Net 30"
                  />
                </FormField>
                <FormField label="Uwagi" hint="Opcjonalne - uwagi do faktury">
                  <input
                    type="text"
                    className="form-control"
                    value={createData.notes}
                    onChange={(e) => setCreateData((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Dodatkowe informacje..."
                  />
                </FormField>
              </div>
              {formTouched && formErrors.items && (
                <div className="field-error" style={{ marginTop: '8px' }}>
                  <FiAlertCircle size={12} /> {formErrors.items}
                </div>
              )}

              {/* Invoice items */}
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h3 style={{ margin: 0 }}>Pozycje faktury</h3>
                  <button className="btn btn-secondary btn-sm" onClick={addItem}>
                    <FiPlus /> Dodaj pozycję
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ID produktu Amazon</th>
                        <th>Opis</th>
                        <th style={{ width: '80px' }}>Ilość</th>
                        <th style={{ width: '100px' }}>Cena jedn.</th>
                        <th style={{ width: '80px' }}>VAT %</th>
                        <th style={{ width: '100px' }}>Wartość</th>
                        <th style={{ width: '40px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {createData.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.amazonProductId}
                              onChange={(e) => updateItem(idx, 'amazonProductId', e.target.value)}
                              placeholder="ASIN / ID"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.description}
                              onChange={(e) => updateItem(idx, 'description', e.target.value)}
                              placeholder="Opis produktu"
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.quantity}
                              min={1}
                              onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.unitPrice}
                              min={0}
                              step={0.01}
                              onChange={(e) => updateItem(idx, 'unitPrice', Number(e.target.value))}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.taxRate}
                              min={0}
                              max={100}
                              onChange={(e) => updateItem(idx, 'taxRate', Number(e.target.value))}
                            />
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 500 }}>
                            {(item.quantity * item.unitPrice).toFixed(2)}
                          </td>
                          <td>
                            {createData.items.length > 1 && (
                              <button
                                className="btn btn-icon btn-sm"
                                onClick={() => removeItem(idx)}
                                title="Usuń pozycję"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600 }}>Netto:</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{itemsTotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right' }}>VAT:</td>
                        <td style={{ textAlign: 'right' }}>{itemsTax.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>Brutto:</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{(itemsTotal + itemsTax).toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                Anuluj
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateSubmit}
                disabled={creating}
              >
                {creating ? 'Tworzenie...' : 'Utwórz fakturę'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel="Wyślij"
        variant="warning"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        loading={bulkSubmitting}
      />
    </div>
  );
};

export default InvoicesPage;
